import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Copy, ExternalLink, Loader2, X } from 'lucide-react'
import { clsx } from 'clsx'
import { TripMap } from '../../map/TripMap'
import { collapseSameLegAlerts } from '../../lib/recurring'
import { istClock, istDateTime } from '../../lib/istFormat'
import { num } from '../../lib/format'
import type { Alert, Severity } from '../../lib/types'
import { tripReplayWindow } from '../../panels/TripReplay'
import { TONE_HEX } from '../../cc/tokens'
import { reportEyebrow, reportPill } from '../ui'
import { kindLabel } from './alertLabels'
import { PATH_WINDOW_HALF_MS, useAlertTrip, type AlertTripData } from './useAlertTrip'
import type { AlertBaseRow } from './alertTypes'

/**
 * The alert in the context of the trip it happened on: a right-hand drawer (same shell
 * as reports/ReportRowDrawer.tsx -- overlay, Escape closes) with the trip's header
 * facts, its map, a scrubber that moves the vehicle marker along the path, and every
 * alert that fired on that leg.
 *
 * Why this is not the Command Centre's replay: that replay is bound to the live store's
 * singleton MapCanvas and to today's live alert tape. A report reader is looking at a
 * leg from last Tuesday, inside a table, possibly for Wonder Cement where no trip rows
 * exist at all -- the footer's "Open in Command Centre replay" is the hand-off for when
 * the full replay (play/speed/follow) is actually wanted.
 */

export interface AlertTripDrawerProps {
  /** The alert to open on; null renders nothing (the drawer owns no open/closed state). */
  alertId: string | null
  onClose: () => void
  /** A click on an alert row or map dot. The drawer highlights locally straight away;
   *  the parent decides whether that also becomes the URL's `?alert=`. */
  onSelectAlert?: (id: string) => void
}

const SEVERITY_HEX: Record<Severity, string> = {
  CRITICAL: TONE_HEX.danger,
  WARN: TONE_HEX.warn,
  INFO: TONE_HEX.info,
}

/** The scrubber's own [start, end]: the replay payload's when there is one (the same
 *  window the Command Centre's bar uses, via tripReplayWindow), else the trip row's, else
 *  the path actually fetched, else the ±30 min the path-window asked for. */
export function alertTripWindow(data: AlertTripData): { start: number; end: number } {
  if (data.replay) return tripReplayWindow(data.replay)
  const t = data.trip.trip
  const alertTs = data.trip.alert.ts
  if (t) return { start: t.started_ts, end: t.ended_ts ?? Math.max(t.started_ts + 1, alertTs) }
  // The path window is ±30 min of whatever the vehicle reported -- a vehicle that went
  // quiet before the alert fired would otherwise give a scrubber that ends before the
  // alert it was opened for. Stretch it to always contain the alert.
  const first = data.path[0]?.ts
  const last = data.path.at(-1)?.ts
  if (first != null && last != null && last > first) {
    return { start: Math.min(first, alertTs), end: Math.max(last, alertTs) }
  }
  return { start: alertTs - PATH_WINDOW_HALF_MS, end: alertTs + PATH_WINDOW_HALF_MS }
}

/** `collapseSameLegAlerts` wants the store's Alert shape; `vehicle_id` + `route_id` are
 *  what it groups an OVERSPEED burst by, so both are carried across. */
export function alertRowToAlert(r: AlertBaseRow): Alert {
  return {
    alert_id: r.alert_id, kind: r.kind, severity: r.severity, ts: r.ts, vehicle_id: r.vehicle_id,
    cell: null, lat: r.lat, lon: r.lon, depot: r.hub_code ?? null, route_id: r.route_id,
    message: r.message ?? '', detail: r.detail, driver_id: r.driver_id, driver_name: r.driver_name,
  }
}

/** The trip's alerts, collapsed like the Alerts tab collapses them, ordered by time --
 *  with the one alert the drawer was opened on guaranteed present as its own row even
 *  if the collapse folded it into a burst's latest sibling (the reader clicked THAT
 *  alert; a list that no longer contains it would read as "wrong trip"). */
export function alertTripList(rows: readonly AlertBaseRow[], focus: AlertBaseRow): Alert[] {
  const all = rows.some((r) => r.alert_id === focus.alert_id) ? rows : [...rows, focus]
  const collapsed = collapseSameLegAlerts(all.map(alertRowToAlert))
  if (!collapsed.some((a) => a.alert_id === focus.alert_id)) collapsed.push(alertRowToAlert(focus))
  return collapsed.sort((a, b) => a.ts - b.ts)
}

function loadTone(loadState: string | null | undefined): 'info' | 'neutral' {
  return loadState?.toUpperCase() === 'LADEN' ? 'info' : 'neutral'
}

export function AlertTripDrawer({ alertId, onClose, onSelectAlert }: AlertTripDrawerProps): JSX.Element | null {
  const { status, error, data } = useAlertTrip(alertId)

  useEffect(() => {
    if (!alertId) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [alertId, onClose])

  // Local highlight, seeded from the prop and reset whenever the prop changes -- so a
  // row click lights up immediately whether or not the parent round-trips it through
  // the URL (useAlertTrip would refetch on a new alertId; the trip is the same either way).
  const [selectedId, setSelectedId] = useState<string | null>(alertId)
  useEffect(() => { setSelectedId(alertId) }, [alertId])

  const scrubWindow = useMemo(() => (data ? alertTripWindow(data) : null), [data])
  // The playhead is derived, not effect-synced: it sits on the opened alert until the
  // reader scrubs or picks another alert for THIS payload. Syncing it in an effect gave
  // every load one extra render with no vehicle marker (playhead null) first.
  const [scrub, setScrub] = useState<{ data: AlertTripData; ts: number } | null>(null)
  const clampTs = useCallback(
    (ts: number) => (scrubWindow ? Math.min(scrubWindow.end, Math.max(scrubWindow.start, ts)) : ts),
    [scrubWindow],
  )
  const playhead: number | null = !data || !scrubWindow
    ? null
    : scrub && scrub.data === data ? scrub.ts : clampTs(data.trip.alert.ts)
  const setPlayhead = useCallback((ts: number) => { if (data) setScrub({ data, ts }) }, [data])

  const list = useMemo(() => (data ? alertTripList(data.trip.alerts, data.trip.alert) : []), [data])

  // A long trip lists dozens of alerts; the one the reader opened may sit below the fold
  // (it is ordered by time, not pinned). Bring it into view once per loaded trip -- by
  // moving the drawer body's own scrollTop, not scrollIntoView, which also scrolls every
  // scrollable ancestor (the page under the overlay jumped on a phone).
  const listRef = useRef<HTMLUListElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const body = bodyRef.current
    if (!data || !body) return
    const id = data.trip.alert.alert_id
    const el = Array.from(listRef.current?.children ?? []).find((li) => (li as HTMLElement).dataset.alertId === id) as HTMLElement | undefined
    if (!el) return
    const top = el.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop
    const bottom = top + el.offsetHeight
    if (bottom > body.scrollTop + body.clientHeight) body.scrollTop = bottom - body.clientHeight + 16
  }, [data])

  const select = useCallback((id: string, ts: number) => {
    setSelectedId(id)
    if (scrubWindow) setPlayhead(clampTs(ts))
    onSelectAlert?.(id)
  }, [onSelectAlert, scrubWindow, setPlayhead, clampTs])

  const onMapSelect = useCallback((id: string) => {
    const a = list.find((x) => x.alert_id === id)
    select(id, a?.ts ?? playhead ?? 0)
  }, [list, select, playhead])

  const [copied, setCopied] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current) }, [])
  const copyLink = useCallback(() => {
    const href = window.location.href
    const write = navigator.clipboard?.writeText(href)
    void (write ?? Promise.reject(new Error('no clipboard'))).then(
      () => {
        setCopied(true)
        if (copyTimer.current) clearTimeout(copyTimer.current)
        copyTimer.current = setTimeout(() => setCopied(false), 1500)
      },
      () => { /* clipboard blocked (http, permissions) -- the URL bar still has it */ },
    )
  }, [])

  if (!alertId) return null

  const trip = data?.trip.trip ?? null
  const alert = data?.trip.alert ?? null
  const driver = data?.trip.driver ?? null
  const tripId = trip?.trip_id ?? null
  const span = scrubWindow ? Math.max(1, scrubWindow.end - scrubWindow.start) : 1
  const noPath = data != null && data.path.length === 0
  const noPin = alert != null && (alert.lat == null || alert.lon == null)

  return (
    <>
      <div className="fixed inset-0 z-40 animate-fade-in bg-slate-900/20" onClick={onClose} />
      <aside
        role="dialog"
        aria-label="Alert trip detail"
        className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[min(720px,100vw)] animate-drawer-in flex-col border-l border-slate-200 bg-white shadow-xl ring-1 ring-black/5"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-6 py-4">
          <div className="min-w-0">
            <div className={reportEyebrow}>{data && !data.trip.trip ? 'Alert · no trip' : 'Alert on trip'}</div>
            {alert ? (
              <>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono text-sm text-slate-900">{alert.vehicle_id ?? '—'}</span>
                  <span className="text-sm text-slate-600">{driver?.driver_label ?? alert.driver_label}</span>
                  {trip?.load_state && <span className={reportPill(loadTone(trip.load_state))}>{trip.load_state}</span>}
                  {trip?.route_id && <span className="font-mono text-xs text-slate-500">{trip.route_id}</span>}
                </div>
                {trip ? (
                  <div className="mt-1 text-sm text-slate-700">
                    <span>{trip.origin_hub ?? '?'} → {trip.destination_hub ?? (trip.ended_ts == null ? 'still travelling' : '?')}</span>
                    <span className="ml-3 font-mono text-xs text-slate-500">
                      {istDateTime(trip.started_ts)} – {trip.ended_ts != null ? istDateTime(trip.ended_ts) : 'open'} IST
                      {trip.distance_odo_km != null && ` · ${num(trip.distance_odo_km, 0)} km`}
                    </span>
                  </div>
                ) : (
                  <div className="mt-1 font-mono text-xs text-slate-500">
                    {kindLabel(alert.kind)} · {istDateTime(alert.ts)} IST
                  </div>
                )}
                {trip == null && (
                  <div className="mt-2 text-xs text-amber-700" data-testid="notice-no-trip">
                    No containing trip for this alert — showing the vehicle's path ±30 min around it.
                  </div>
                )}
                {trip?.source === 'geo_leg' && (
                  <div className="mt-2 text-xs text-amber-700" data-testid="notice-geo-leg">
                    Leg inferred from geofence crossings (no trip record) — path shown ±30 min around the alert.
                  </div>
                )}
                {noPath && noPin && (
                  <div className="mt-2 text-xs text-rose-700" data-testid="notice-no-data">
                    No trip or GPS path recorded for this alert, and the alert itself has no position.
                  </div>
                )}
              </>
            ) : (
              <div className="mt-1 font-mono text-sm text-slate-400">{alertId}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 transition-[color,transform] duration-100 hover:text-slate-700 active:scale-95"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div ref={bodyRef} className="min-h-0 flex-1 overflow-auto overscroll-contain">
          {status === 'loading' && (
            <div className="flex items-center gap-2 px-6 py-8 text-sm text-slate-500" role="status">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading trip…
            </div>
          )}
          {status === 'error' && (
            <div className="px-6 py-8 text-sm text-rose-700" role="alert">
              Could not load this alert's trip: {error}
            </div>
          )}
          {data && alert && scrubWindow && (
            <>
              <div className="h-72 border-b border-slate-200">
                <TripMap
                  points={data.path}
                  events={data.replay?.events ?? []}
                  alerts={list}
                  selectedAlertId={selectedId}
                  onSelectAlert={onMapSelect}
                  playhead={playhead}
                  fitKey={`${alert.alert_id}|${tripId ?? 'window'}`}
                />
              </div>

              {/* Scrubber: the same x = (ts - start) / span mapping TripReplayBar's ticks
                  use, over the drawer's own window -- a plain range input rather than
                  the bar itself, which carries play/speed/follow controls that need the
                  live frame loop this drawer deliberately does not run. */}
              <div className="px-6 pt-4">
                <div className="relative">
                  <input
                    type="range"
                    aria-label="Trip time"
                    min={scrubWindow.start}
                    max={scrubWindow.end}
                    step={1000}
                    value={playhead ?? scrubWindow.start}
                    onChange={(e) => setPlayhead(Number(e.target.value))}
                    className="w-full accent-blue-600"
                  />
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-5" aria-hidden="true">
                    {list.filter((a) => a.ts >= scrubWindow.start && a.ts <= scrubWindow.end).map((a) => (
                      <span
                        key={a.alert_id}
                        className={clsx(
                          'absolute top-0 h-2 w-2 -translate-x-1/2 rounded-full ring-1 ring-white',
                          a.alert_id === selectedId && 'h-3 w-3 ring-2',
                        )}
                        style={{ left: `${((a.ts - scrubWindow.start) / span) * 100}%`, background: SEVERITY_HEX[a.severity] ?? TONE_HEX.info }}
                        title={`${kindLabel(a.kind)} · ${istClock(a.ts)}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="mt-1 flex justify-between font-mono text-[11px] text-slate-500">
                  <span>{istClock(scrubWindow.start)}</span>
                  <span className="text-slate-900">{istClock(playhead)} IST</span>
                  <span>{istClock(scrubWindow.end)}</span>
                </div>
              </div>

              <div className="px-6 pb-4 pt-4">
                <div className={reportEyebrow}>
                  {list.length} alert{list.length === 1 ? '' : 's'} on this {tripId ? 'trip' : 'window'}
                </div>
                <ul ref={listRef} className="mt-2 divide-y divide-slate-100" data-testid="trip-alert-list">
                  {list.map((a) => {
                    const selected = a.alert_id === selectedId
                    return (
                      <li key={a.alert_id} data-alert-id={a.alert_id}>
                        <button
                          type="button"
                          onClick={() => select(a.alert_id, a.ts)}
                          aria-current={selected ? 'true' : undefined}
                          className={clsx(
                            'flex w-full items-start gap-3 rounded-md px-2 py-2 text-left transition-colors',
                            selected ? 'bg-sky-50 ring-1 ring-inset ring-sky-200' : 'hover:bg-slate-50',
                          )}
                        >
                          <span
                            className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ background: SEVERITY_HEX[a.severity] ?? TONE_HEX.info }}
                            aria-hidden="true"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-baseline gap-x-2">
                              <span className="text-sm font-medium text-slate-900" title={a.kind}>{kindLabel(a.kind)}</span>
                              {a._groupCount != null && a._groupCount > 1 && (
                                <span className="text-xs text-slate-500">×{a._groupCount}</span>
                              )}
                              <span className="font-mono text-xs text-slate-500">{istClock(a.ts)}</span>
                              {a.lat == null && <span className="text-[10.5px] text-slate-400">no position</span>}
                            </span>
                            <span className="block truncate text-xs text-slate-600">{a.message}</span>
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </>
          )}
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 px-6 py-3">
          {tripId ? (
            <Link
              to={`/fleet?trip=${encodeURIComponent(tripId)}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition-colors hover:text-slate-900"
            >
              <ExternalLink className="h-4 w-4" /> Open in Command Centre replay
            </Link>
          ) : (
            <span
              aria-disabled="true"
              title="No trip record to replay"
              className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-400 ring-1 ring-inset ring-slate-200"
            >
              <ExternalLink className="h-4 w-4" /> Open in Command Centre replay
            </span>
          )}
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition-colors hover:text-slate-900"
          >
            <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy link'}
          </button>
        </footer>
      </aside>
    </>
  )
}
